import React from "react";
import { useEffect, useState } from "react";
import { useLanguage } from "../../../context/LanguageContext";
import axios from "axios";
import { BaseUrlApi, ErrorMessage } from "../../../lib/api";
import { toast } from "sonner";
import LoaderPage from "../../../components/LoaderPage";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import {
  ArrowUpDown,
  CalendarDays,
  CheckCircle,
  Eye,
  Info,
  XCircle,
} from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { DataTable } from "../../../components/DataTable";
import { DateFormate } from "../../../lib/utils";
import { useAuth } from "../../../context/AuthContext";
import { LessonTeacherData } from "../../../lib/languages";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Spin } from "antd";

const filterOptions = [
  { value: "all", label: "All Lessons" },
  { value: "today", label: "Today's Lessons" },
  { value: "upcoming", label: "Upcoming Lessons" },
  { value: "past", label: "Past Lessons" },
];

// Determine badge info based on status
const getStatusInfo = (status) => {
  switch (status) {
    case 2:
      return {
        text: "Today",
        color: "text-blue-500 bg-blue-50",
        icon: CalendarDays,
      };
    case 1:
      return {
        text: "Upcoming",
        color: "text-green-500 bg-green-50",
        icon: CheckCircle,
      };
    case 0:
      return { text: "Past", color: "text-red-500 bg-red-50", icon: XCircle };
    default:
      return { text: "Unknown", color: "text-gray-500 bg-gray-50", icon: Info };
  }
};

export default function Lessons() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();

  const [loadingLessons, setLoadingLessons] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [filter, setFilter] = useState("all");

  // Fetch student lessons
  useEffect(() => {
    setLoadingLessons(true);
    axios
      .get(`${BaseUrlApi}/student/lessons`)
      .then(({ data }) => {
        setLessons(data.data);
      })
      .catch((error) => {
        toast("Error loading lessons", {
          description: ErrorMessage(error),
        });
      })
      .finally(() => setLoadingLessons(false));
  }, []);

  // Filter lessons by booking.date
  const filteredLessons = lessons.filter((lesson) => {
    const dateStr = lesson.booking?.date;
    const now = new Date();
    if (!dateStr) return filter === "all";
    const lessonDate = new Date(dateStr);
    const lessonDay = lessonDate.toDateString();
    const today = now.toDateString();

    if (filter === "today") return lessonDay === today;
    if (filter === "upcoming") return lessonDate > now && lessonDay !== today;
    if (filter === "past") return lessonDate < now && lessonDay !== today;
    return true;
  });

  const columns = [
    {
      accessorKey: "title.en",
      header: "Title (EN)",
      cell: ({ row }) => row.original.title?.en || "---",
    },
    {
      accessorKey: "title.ar",
      header: "Title (AR)",
      cell: ({ row }) => row.original.title?.ar || "---",
    },
    {
      accessorKey: "group.name",
      header: "Group",
    },
    {
      accessorKey: "booking.date",
      header: "Lesson Date",
      cell: ({ row }) => row.original.booking?.date || "---",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const dateStr = row.original.booking?.date;
        const lessonDate = dateStr ? new Date(dateStr) : null;
        const now = new Date();
        const statusValue = lessonDate ? (lessonDate > now ? 1 : 0) : -1;
        const { text, color, icon: StatusIcon } = getStatusInfo(statusValue);
        return (
          <Badge variant="outline" className={color}>
            <StatusIcon size={12} className="mx-1" />
            {text}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="flex items-center gap-2">
            <Button
              size="icon"
              className="rounded-xl h-9"
              onClick={() =>
                navigate(`/lesson-view/${item.id}`, {
                  state: {
                    lessonData: item,
                    courseData: item.group.level.course,
                  },
                })
              }
            >
              <Eye />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6" dir={language === "en" ? "ltr" : "rtl"}>
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Student Lessons</h2>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter Lessons" />
          </SelectTrigger>
          <SelectContent>
            {filterOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {loadingLessons ? (
        <div className="flex items-center justify-center h-40">
          <Spin size="large" />
        </div>
      ) : (
        <DataTable columns={columns} data={filteredLessons} title="Lessons" />
      )}
    </div>
  );
}
